import { dia, sumarDias } from './formato';

/** `2026-09-14` → `14 sep`: un día de calendario, sin que la zona le reste uno. */
export function fechaDeCalendario(calendario: string): string {
  // Al mediodía: a medianoche de Greenwich, en Lima todavía era el día anterior.
  return dia(`${calendario}T12:00:00Z`);
}

/** Cuántos días hay de `desde` a `hasta`, los dos `2026-09-14`. */
export function diasEntre(desde: string, hasta: string): number {
  const inicio = Date.parse(`${desde}T12:00:00Z`);
  const fin = Date.parse(`${hasta}T12:00:00Z`);
  return Math.round((fin - inicio) / 86_400_000);
}

export type EstadoDelVencimiento = 'vencido' | 'hoy' | 'pronto' | 'bien';

/**
 * Cómo está una fecha de vencimiento respecto a hoy, en Lima.
 *
 * `pronto` es dentro del margen de aviso: lo que entra hoy y vence pasado
 * mañana se puede recibir, pero conviene que quien lo recibe lo vea antes de
 * guardarlo al fondo de la cámara.
 */
export function estadoDelVencimiento(
  vence: string,
  hoy: string,
  margenDias = 3,
): EstadoDelVencimiento {
  const faltan = diasEntre(hoy, vence);
  if (faltan < 0) return 'vencido';
  if (faltan === 0) return 'hoy';
  if (faltan <= margenDias) return 'pronto';
  return 'bien';
}

/** «vence hoy», «en 6 días», «venció hace 2 días». */
export function cuantoFalta(vence: string, hoy: string): string {
  const faltan = diasEntre(hoy, vence);
  if (faltan === 0) return 'vence hoy';
  if (faltan === 1) return 'vence mañana';
  if (faltan > 1) return `en ${faltan} días`;
  if (faltan === -1) return 'venció ayer';
  return `venció hace ${-faltan} días`;
}

/** Los atajos de la fecha de vencimiento, en días desde hoy. */
export const ATAJOS_DE_VENCIMIENTO: readonly { texto: string; dias: number }[] = [
  { texto: '+3 días', dias: 3 },
  { texto: '+1 semana', dias: 7 },
  { texto: '+15 días', dias: 15 },
  { texto: '+1 mes', dias: 30 },
  { texto: '+3 meses', dias: 90 },
];

/** La fecha que propone la ficha del insumo: hoy más lo que dura. */
export function vencimientoSugerido(hoy: string, vidaUtilDias: number | null): string | null {
  return vidaUtilDias === null || vidaUtilDias <= 0 ? null : sumarDias(hoy, vidaUtilDias);
}

/**
 * El vencimiento como viene impreso en la caja: «EXP 11/2027», «VTO 03-28»,
 * «11/2027». Vence el **último día** de ese mes: así lo lee la DIGEMID y así
 * lo lee el químico. Nulo si no es eso.
 *
 * Se pedía la fecha entera, y quien recibía con la caja en la mano ponía el
 * día 1 —un mes de vida menos para cada lote— o lo dejaba en el calendario
 * contando casillas.
 */
export function vencimientoDeLaCaja(texto: string): string | null {
  const limpio = texto
    .trim()
    .replace(/^(exp|vto|venc|vence|cad|v)\b\.?\s*:?\s*/i, '')
    .trim();
  const mesYAnio = /^(\d{1,2})\s*[/.-]\s*(\d{2}|\d{4})$/.exec(limpio);
  if (mesYAnio === null) return null;

  const mes = Number(mesYAnio[1]);
  if (mes < 1 || mes > 12) return null;
  const anio = mesYAnio[2]!.length === 2 ? 2000 + Number(mesYAnio[2]) : Number(mesYAnio[2]);
  // El día cero del mes siguiente es el último de este, también en febrero.
  return new Date(Date.UTC(anio, mes, 0, 12)).toISOString().slice(0, 10);
}
